/* @license
 * Copyright 2026 k1pp0
 * SPDX-License-Identifier: MIT
 */
const subscribers = new Set();
const symbolCache = new WeakMap();
let globalPatchesInstalled = false;
let frameHookInstalled = false;
let cachedARRenderer = null;
const NAVIGATOR_XR_MARKER = '__captureXRRequestSessionWrapped';
const FRAME_HOOK_MARKER = '__captureOnWebXRFrameWrapped';
/**
 * Walks an object's own properties and prototype chain looking for a symbol
 * whose `.description` matches `description`. Caches results by (proto, desc).
 */
export function findSymbol(obj, description) {
    var _a;
    if (obj == null || (typeof obj !== 'object' && typeof obj !== 'function')) {
        return null;
    }
    let target = obj;
    while (target != null) {
        let perTarget = symbolCache.get(target);
        if (perTarget == null) {
            perTarget = new Map();
            symbolCache.set(target, perTarget);
        }
        if (perTarget.has(description)) {
            const cached = (_a = perTarget.get(description)) !== null && _a !== void 0 ? _a : null;
            if (cached != null) {
                return cached;
            }
        }
        else {
            const own = Object.getOwnPropertySymbols(target).find((s) => s.description === description);
            perTarget.set(description, own !== null && own !== void 0 ? own : null);
            if (own != null) {
                return own;
            }
        }
        target = Object.getPrototypeOf(target);
    }
    return null;
}
function readSymbolProperty(host, description, name) {
    const sym = findSymbol(host, description);
    if (sym == null) {
        throw new Error(`[model-viewer-webxr-capture] Could not find ${name} on the host ` +
            '<model-viewer>. This usually means @google/model-viewer was ' +
            'upgraded to an incompatible major version. Pin to ^4.3.1 or file ' +
            'an issue at https://github.com/k1pp0/model-viewer-webxr-capture/issues.');
    }
    return host[sym];
}
/**
 * Returns the host element's internal `ModelScene` (a three.js Scene).
 */
export function getModelScene(host) {
    return readSymbolProperty(host, 'scene', '$scene');
}
/**
 * Returns the host's internal `Renderer` (the wrapper that owns the
 * `WebGLRenderer` and the singleton `ARRenderer`).
 */
export function getRenderer(host) {
    return readSymbolProperty(host, 'renderer', '$renderer');
}
export function getThreeRenderer(host) {
    return getRenderer(host).threeRenderer;
}
export function getARRenderer(host) {
    return getRenderer(host).arRenderer;
}
/**
 * Returns the live `XRSession` if one is active on the host's renderer,
 * otherwise `null`.
 */
export function getXRSession(host) {
    const xr = getThreeRenderer(host).xr;
    return xr.getSession();
}
/**
 * Idempotently installs the `navigator.xr.requestSession` wrapper that appends
 * `'camera-access'` to the `optionalFeatures` of every `'immersive-ar'`
 * session request.
 *
 * This is a global side effect. It is gated on the first plugin connect, so
 * pages that load the bundle but do not use `<model-viewer-webxr-capture>`
 * see no behavior change.
 */
export function ensureGlobalPatches() {
    if (globalPatchesInstalled) {
        return;
    }
    globalPatchesInstalled = true;
    const xr = (typeof navigator !== 'undefined' ? navigator.xr : null);
    if (xr != null && xr[NAVIGATOR_XR_MARKER] !== true &&
        typeof xr.requestSession === 'function') {
        const original = xr.requestSession.bind(xr);
        xr.requestSession = function (mode, options) {
            if (mode === 'immersive-ar') {
                const opts = options !== null && options !== void 0 ? options : {};
                const optional = Array.isArray(opts.optionalFeatures) ?
                    opts.optionalFeatures.slice() :
                    [];
                if (optional.indexOf('camera-access') === -1) {
                    optional.push('camera-access');
                }
                return original(mode, Object.assign(Object.assign({}, opts), { optionalFeatures: optional }));
            }
            return original(mode, options);
        };
        xr[NAVIGATOR_XR_MARKER] = true;
    }
}
/**
 * Idempotently wraps `arRenderer.onWebXRFrame` so that, after the original
 * frame logic runs, every subscriber whose host's scene equals the current
 * `presentedScene` is invoked with `(firstView, scene, viewCamera)`.
 *
 * `arRenderer` is a process-wide singleton (`Renderer.singleton.arRenderer`),
 * so the wrapper is installed exactly once for the entire page.
 */
export function ensureFrameHook(host) {
    if (frameHookInstalled) {
        return;
    }
    const arRenderer = getARRenderer(host);
    cachedARRenderer = arRenderer;
    if (arRenderer.onWebXRFrame[FRAME_HOOK_MARKER] === true) {
        frameHookInstalled = true;
        return;
    }
    const original = arRenderer.onWebXRFrame.bind(arRenderer);
    const wrapped = function (time, frame) {
        original(time, frame);
        dispatchFrame(arRenderer);
    };
    wrapped[FRAME_HOOK_MARKER] = true;
    arRenderer.onWebXRFrame = wrapped;
    frameHookInstalled = true;
}
function dispatchFrame(arRenderer) {
    if (subscribers.size === 0) {
        return;
    }
    const scene = arRenderer.presentedScene;
    if (scene == null) {
        return;
    }
    const frame = arRenderer.frame;
    if (frame == null) {
        return;
    }
    const xr = arRenderer.threeRenderer.xr;
    const refSpace = xr.getReferenceSpace();
    if (refSpace == null) {
        return;
    }
    const pose = frame.getViewerPose(refSpace);
    if (pose == null || pose.views.length === 0) {
        return;
    }
    const view = pose.views[0];
    const xrCamera = xr.getCamera();
    const cameras = xrCamera.cameras;
    const viewCamera = cameras != null && cameras.length > 0 ? cameras[0] : xrCamera;
    for (const sub of subscribers) {
        let hostScene = null;
        try {
            hostScene = getModelScene(sub.host);
        }
        catch (_a) {
            continue;
        }
        if (hostScene === scene) {
            try {
                sub.callback(view, scene, viewCamera);
            }
            catch (e) {
                console.warn('[model-viewer-webxr-capture] frame subscriber threw:', e);
            }
        }
    }
}
/**
 * Subscribes a callback that fires once per AR frame for the matching host
 * (i.e. when `arRenderer.presentedScene === host[$scene]`). Returns an
 * unsubscribe function. Safe to call multiple times for the same host.
 */
export function subscribe(host, callback) {
    const entry = { host, callback };
    subscribers.add(entry);
    return () => {
        subscribers.delete(entry);
    };
}
/**
 * For tests. Returns the singleton ARRenderer last seen by the bridge, or
 * null if no plugin has connected yet.
 */
export function _getCachedARRenderer() {
    return cachedARRenderer;
}
/**
 * For tests. Returns the live subscriber count.
 */
export function _getSubscriberCount() {
    return subscribers.size;
}
//# sourceMappingURL=host-bridge.js.map